// Registers this device for push notifications and keeps the backend's device
// token in sync with the auth session. ONE code path for all three platforms:
// `@capacitor-firebase/messaging` mints an FCM token on web, iOS and Android
// alike, so the backend's firebase-admin sender (which speaks FCM) reaches every
// device with no APNs/FCM bridging on our side.
//
// - web    : Firebase JS SDK under the hood → needs the app initialised + the
//            firebase-messaging-sw.js service worker registered.
// - ios    : the plugin calls FirebaseApp.configure() itself; needs
//            GoogleService-Info.plist + Push Notifications capability.
// - android: needs google-services.json (the gradle plugin is already wired).
//
// No-op until Firebase is configured (VITE_FIREBASE_API_KEY empty), so dev
// builds without credentials run clean.

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';
import { f7 } from 'framework7-react';
import { openapiClient } from '@/api/openapi-client';
import { getT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { navigateDeepLink } from '@/utils/navigateMain';
import { env } from '@/env';
import { trackEvent } from '@/lib/analytics/umami-client';
import { notificationDeepLink } from '@/features/notifications/lib/deepLink';
import { collectDeviceHealth } from '@/features/notifications/lib/deviceHealth';
import {
  claimHealthReport,
  notificationHealthPayload,
} from '@/features/notifications/lib/notificationHealth';
import { markPushOpen } from '@/features/notifications/lib/pushOpen';

const firebaseConfig = {
  apiKey: env.firebase.apiKey,
  authDomain: env.firebase.authDomain,
  projectId: env.firebase.projectId,
  storageBucket: env.firebase.storageBucket,
  messagingSenderId: env.firebase.messagingSenderId,
  appId: env.firebase.appId,
};

export type PushPlatform = 'web' | 'ios' | 'android';

export function currentPlatform(): PushPlatform {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android' ? p : 'web';
}

// Whether this build ships Firebase credentials at all. When false there is no
// push transport, so the settings UI can say so instead of showing a dead
// "not enabled" state the user could never fix.
export const isPushConfigured = Boolean(firebaseConfig.apiKey);

// Web needs the Firebase JS app initialised and the messaging service worker
// registered before the plugin can mint a token. The config is handed to the SW
// via its query string so the static file in /public needs no build-time
// templating - it reads `self.location.search` at runtime.
async function ensureWebMessaging(): Promise<ServiceWorkerRegistration | undefined> {
  if (!getApps().length) initializeApp(firebaseConfig);
  if (!('serviceWorker' in navigator)) return undefined;
  const params = new URLSearchParams(firebaseConfig).toString();
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`);
}

// Module-level so register (on login) and unregister (on logout) share the live
// token + listener handles across effect runs.
let registered = false;
let currentToken: string | null = null;
let pushServiceBlocked = false;
const listenerHandles: Promise<{ remove: () => void }>[] = [];

const PUSH_BLOCKED_HINT_KEY = 'push-blocked-hint-shown';

export const isPushServiceBlocked = (): boolean => pushServiceBlocked;

function storageOrUndefined(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function showPushBlockedHintOnce(): void {
  const storage = storageOrUndefined();
  try {
    if (storage?.getItem(PUSH_BLOCKED_HINT_KEY)) return;
  } catch {
    return;
  }
  try {
    f7.toast.show({
      text: getT().notifications.statusBrowserBlockedDetail,
      closeButton: true,
      position: 'bottom',
    });
    storage?.setItem(PUSH_BLOCKED_HINT_KEY, '1');
  } catch {
    /* best effort */
  }
}

async function registerWithHealth(token: string, platform: PushPlatform): Promise<boolean> {
  const health = await collectDeviceHealth();
  const { data, response } = await openapiClient.POST('/api/device-tokens', {
    body: { token, platform, health },
  });
  if (!data || response.status !== 201) {
    console.warn(`[push] device token registration failed - backend answered ${response.status}`);
    return false;
  }
  const payload = notificationHealthPayload({
    platform,
    healthState: data.data.healthState,
    health,
  });
  if (claimHealthReport(payload, storageOrUndefined())) trackEvent('notification_health', payload);
  return true;
}

// Re-report this device's health without re-minting a token. Called when the app
// returns to the foreground, because the settings that decide whether a push
// makes a sound are changed in the OS — outside the app, while it is
// backgrounded — so login-time reporting alone would show a stale verdict for as
// long as the session lasts.
export async function reportDeviceHealth(): Promise<void> {
  if (!currentToken) return;
  try {
    await registerWithHealth(currentToken, currentPlatform());
  } catch {
    /* best effort */
  }
}

// Exported so the notification settings screen can (re-)trigger the OS/browser
// permission prompt on demand, not only implicitly at login.
export async function registerPush(): Promise<void> {
  if (registered) return;
  if (!firebaseConfig.apiKey) {
    console.warn('[push] skipped - VITE_FIREBASE_API_KEY not set (Firebase not configured)');
    return;
  }
  registered = true;

  try {
    const platform = currentPlatform();
    // On web the Capacitor plugin proxies to the Firebase JS SDK, whose very
    // first call (requestPermissions → getMessaging) needs an initialised app.
    // So init the web app + service worker BEFORE any FirebaseMessaging call,
    // otherwise it throws "No Firebase App '[DEFAULT]' has been created".
    const serviceWorkerRegistration = platform === 'web' ? await ensureWebMessaging() : undefined;
    if (platform === 'web') {
      console.info(
        `[push] Firebase web app initialised (project: ${firebaseConfig.projectId}); service worker ${serviceWorkerRegistration ? 'registered' : 'unavailable'}`,
      );
    }

    const permission = await FirebaseMessaging.requestPermissions();
    if (permission.receive !== 'granted') {
      console.info(`[push] notification permission not granted (${permission.receive})`);
      registered = false;
      return;
    }

    // Android 8+ binds the notification sound to the CHANNEL, not the FCM
    // payload. Create a high-importance "alerts" channel that uses our loud
    // custom alarm sound (res/raw/alert) so the backend can route time-sensitive
    // notifications (new order, new booking) to it and actually grab attention.
    // Idempotent - re-creating an existing channel is a no-op. (iOS reads the
    // sound filename straight from the payload, so no channel needed there.)
    if (platform === 'android') {
      try {
        await FirebaseMessaging.createChannel({
          id: 'alerts',
          name: 'Important alerts',
          description: 'Time-sensitive alerts like new orders and bookings',
          sound: 'alert',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
        });
      } catch (err) {
        console.warn('[push] failed to create alerts channel', err);
      }
    }

    const { token } = await FirebaseMessaging.getToken(
      platform === 'web' ? { vapidKey: env.firebase.vapidKey, serviceWorkerRegistration } : {},
    );
    if (!token) {
      console.warn('[push] no FCM token returned');
      registered = false;
      return;
    }

    currentToken = token;
    pushServiceBlocked = false;
    // Registration doubles as the device's health check-in. The backend turns
    // this into `push_device_health{platform,state}` — the only signal that can
    // distinguish "never arrived" from "arrived silently", because both are
    // decided by settings inside this phone that no server-side signal can see.
    const registeredWithBackend = await registerWithHealth(token, platform);
    if (registeredWithBackend) {
      console.info(
        `[push] ready - ${platform} token registered with backend (${token.slice(0, 12)}…)`,
      );
    }

    // FCM may rotate the token at any time - re-register so the backend always
    // points at a live token for this device.
    listenerHandles.push(
      FirebaseMessaging.addListener('tokenReceived', ({ token: next }) => {
        if (!next || next === currentToken) return;
        currentToken = next;
        void openapiClient
          .POST('/api/device-tokens', { body: { token: next, platform } })
          .then(({ data, response }) => {
            if (!data) {
              console.warn(
                `[push] device token registration failed - backend answered ${response.status}`,
              );
            }
          });
      }),
    );

    // Native tap → open the deep-link target the dispatcher embedded in `data`.
    // Web taps are handled by the service worker's `notificationclick`.
    listenerHandles.push(
      FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        const data: Record<string, unknown> = { ...(event.notification?.data ?? {}) };
        const path = notificationDeepLink(data);
        if (path) {
          markPushOpen(path);
          navigateDeepLink(path);
        }
      }),
    );
  } catch (error) {
    registered = false;
    if ('brave' in navigator && error instanceof DOMException && error.name === 'AbortError') {
      pushServiceBlocked = true;
      console.warn('[push] browser blocks the push service', error);
      showPushBlockedHintOnce();
      return;
    }
    console.error('Push registration failed', error);
  }
}

// Detach this device from push. MUST be awaited BEFORE the auth session is
// cleared: the server DELETE is an authenticated call, so if it runs after
// logout() has nulled the JWT it goes out with no Authorization header → 401 and
// the DeviceToken row leaks (stays active, mapped to the signed-out user). The
// sign-out handler calls this first; the auth-state effect below is only a
// fallback for session-expiry logouts (token already dead — nothing we can do).
export async function unregisterPush(): Promise<void> {
  for (const handle of listenerHandles.splice(0)) {
    try {
      (await handle).remove();
    } catch {
      /* listener already gone */
    }
  }
  if (currentToken) {
    const token = currentToken;
    currentToken = null;
    // Server detach — best effort; a stale row is pruned server-side when FCM
    // later reports the token dead.
    if (useAuthStore.getState().accessToken) {
      try {
        await openapiClient.DELETE('/api/device-tokens/{token}', { params: { path: { token } } });
      } catch {
        /* best effort - server prunes stale tokens */
      }
    }
    // Kill the FCM token in its OWN try, so a failed server DELETE never skips
    // it — otherwise the device keeps a live token bound to the old user.
    try {
      await FirebaseMessaging.deleteToken();
    } catch {
      /* token already gone / plugin unavailable */
    }
  }
  registered = false;
}

export function usePushRegistration() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      void registerPush();
    } else {
      void unregisterPush();
    }
  }, [isAuthenticated]);

  // Notification permission and the android alerts channel are changed in OS
  // settings — outside this app, while it is backgrounded. Re-reading them on
  // every return to the foreground is what keeps the health dashboard showing
  // the CURRENT state of a device rather than whatever it was at login.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reportDeviceHealth();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated]);
}
